import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Star, ShoppingCart, Filter, Truck, Award, Shield, Zap, Phone, Download, ChevronDown, Search, Grid, List } from 'lucide-react';

import Img1 from '../components/images/Industrialweighing/bench scale.png'
import Img2 from '../components/images/Industrialweighing/counter scale.jpg'
import Img3 from '../components/images/Industrialweighing/crane scale.avif'
import Img4 from '../components/images/Industrialweighing/platform scale.avif'
import Img5 from '../components/images/Industrialweighing/waterproof.jpg'
import Img6 from '../components/images/Industrialweighing/pallet scale.png'
import Img7 from '../components/images/Industrialweighing/indust1.png'

export default function IndustrialScalesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [showFilter, setShowFilter] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState({});

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Enhanced product data with more professional details
  const scales = [
    {
      id: 1,
      name: "Heavy Duty Platform Scale HD-5000",
      category: "platform",
      capacity: "5,000 kg",
      accuracy: "±0.1%",
      platform: "120 × 120 cm",
      price: 2499.99,
      originalPrice: 2999.99,
      rating: 4.8,
      reviews: 124,
      image: Img4,
      featured: true,
      inStock: true,
      certifications: ["ISO 9001", "CE", "OIML"],
      description: "Industrial-grade platform scale engineered for extreme durability and precision in demanding warehouse environments.",
      features: ["Stainless steel construction", "IP67 waterproof rating", "Wireless connectivity", "5-year warranty"]
    },
    {
      id: 2,
      name: "Precision Digital Bench Scale DB-150",
      category: "bench",
      capacity: "150 kg",
      accuracy: "±0.05%",
      platform: "45 × 60 cm",
      price: 899.99,
      originalPrice: 1099.99,
      rating: 4.6,
      reviews: 89,
      image: Img1,
      featured: false,
      inStock: true,
      certifications: ["ISO 9001", "FDA"],
      description: "High-precision bench scale with advanced digital display for quality control and laboratory applications.",
      features: ["LCD touchscreen", "USB connectivity", "Data logging", "Calibration certificate"]
    },
    {
        id: 3,
        name: "Industrial Counting Scale CS-500",
        category: "counting",
        capacity: "50 kg",
        accuracy: "1 g",
        platform: "35 × 45 cm",
        price: 899.99,
        originalPrice: 1099.99,
        rating: 4.8,
        reviews: 124,
        image: Img2,  // replace with actual counting scale image
        featured: true,
        inStock: true,
        certifications: ["CE", "ISO", "OIML"],
        description: "High-precision industrial counting scale designed for fast, accurate part counting and inventory control.",
        features: [
            "Dual display for weight and piece count",
            "High-resolution sensors",
            "Memory for multiple sample weights",
            "Durable industrial design",
            "Battery and AC operation"
        ]
    },
    {
        id: 4,
        name: "Industrial Waterproof Scale WS-300",
        category: "waterproof",
        capacity: "30 kg",
        accuracy: "0.5 g",
        platform: "30 × 40 cm",
        price: 1199.99,
        originalPrice: 1399.99,
        rating: 4.9,
        reviews: 98,
        image: Img5,  // replace with actual waterproof scale image
        featured: true,
        inStock: true,
        certifications: ["IP68", "CE", "OIML"],
        description: "Rugged waterproof industrial scale built to withstand harsh environments such as food processing, chemical, and marine industries.",
        features: [
            "IP68-rated waterproof and dustproof design",
            "Stainless steel housing and platform",
            "High-precision load cell technology",
            "Rechargeable battery with long standby time",
            "Ideal for wet and industrial environments"
        ]
    },
    {
      id: 5,
      name: "Heavy-Duty Crane Scale CS-10000",
      category: "crane",
      capacity: "10,000 kg",
      accuracy: "±0.2%",
      platform: "Hanging scale",
      price: 4599.99,
      originalPrice: 5299.99,
      rating: 4.5,
      reviews: 41,
      image: Img3,
      featured: true,
      inStock: true,
      certifications: ["ASME", "CE", "FCC"],
      description: "Professional crane scale with wireless remote display and advanced safety features for lifting operations.",
      features: ["Wireless remote", "360° rotating hook", "Overload protection", "Long battery life"]
    },
    {
      id: 6,
      name: "Integrated Pallet Jack Scale PJ-2500",
      category: "pallet",
      capacity: "2,500 kg",
      accuracy: "±0.1%",
      platform: "Fork: 160 × 55 cm",
      price: 3899.99,
      originalPrice: 4399.99,
      rating: 4.6,
      reviews: 63,
      image: Img6,
      featured: false,
      inStock: true,
      certifications: ["NTEP", "OIML", "CE"],
      description: "Revolutionary weighing pallet jack combining material handling efficiency with precise weight measurement.",
      features: ["Ergonomic handle", "Digital display", "Printer connectivity", "Rechargeable battery"]
    }
  ];

  // Filter products
  const filteredScales = scales.filter(scale => {
    const matchesCategory = activeTab === 'all' || 
      (activeTab === 'featured' && scale.featured) || 
      scale.category === activeTab;
    const matchesSearch = scale.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scale.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Enhanced categories
  const categories = [
    { id: 'all', name: 'All Products', count: scales.length },
    { id: 'featured', name: 'Featured', count: scales.filter(s => s.featured).length },
    { id: 'platform', name: 'Platform Scales', count: scales.filter(s => s.category === 'platform').length },
    { id: 'floor', name: 'Floor Scales', count: scales.filter(s => s.category === 'floor').length },
    { id: 'bench', name: 'Bench Scales', count: scales.filter(s => s.category === 'bench').length },
    { id: 'crane', name: 'Crane Scales', count: scales.filter(s => s.category === 'crane').length },
    { id: 'precision', name: 'Precision Balances', count: scales.filter(s => s.category === 'precision').length }
  ];

  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    });
  };

  const calculateDiscount = (original, current) => {
    return Math.round(((original - current) / original) * 100);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={`${i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-2 text-sm font-semibold text-gray-700">{rating}</span>
        <span className="text-sm text-gray-500">({scales.find(s => s.rating === rating)?.reviews || 0})</span>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen font-poppins">
      {/* Enhanced Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        
        <div className="container mx-auto px-4 py-24 relative z-10">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-12 lg:mb-0">
              <div className="inline-flex items-center bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 mb-6">
                <Award className="w-4 h-4 mr-2 text-blue-300" />
                <span className="text-sm font-medium text-blue-100">Industry Leading Precision</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                Professional Industrial
                <span className="block">Weighing Solutions</span>
              </h1>
              
              <p className="text-xl lg:text-2xl mb-8 text-gray-300 leading-relaxed">
                Precision-engineered scales for manufacturing, warehousing, and logistics operations with uncompromising accuracy and durability.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center">
                  Explore Products 
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="border-2 border-white/30 backdrop-blur-sm hover:bg-white/10 px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Request Quote
                </button>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/20">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">500+</div>
                  <div className="text-sm text-gray-300">Industrial Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">99.9%</div>
                  <div className="text-sm text-gray-300">Accuracy Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-1">24/7</div>
                  <div className="text-sm text-gray-300">Support Available</div>
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/2 relative">
              <div className="relative  p-8">
                <img
                  src={Img7}
                  alt="Professional Industrial Scale"
                  className="rounded-2xl shadow-2xl w-full object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-green-400 to-blue-500 text-white p-4 rounded-2xl shadow-xl">
                  <div className="text-sm font-medium">Certified Quality</div>
                  <div className="text-xs opacity-90">ISO 9001 | CE | OIML</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Features Section */}
      <div className="container mx-auto px-4 py-20">
        <div 
          id="features"
          data-animate
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent">
            Why Industry Leaders Choose Our Scales
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Delivering unmatched precision, reliability, and innovation in industrial weighing solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Shield className="text-blue-600" size={32} />,
              title: "Unmatched Precision",
              description: "Industry-leading accuracy with advanced load cell technology and environmental compensation for consistent, reliable measurements in any condition.",
              color: "from-blue-500/10 to-blue-600/10",
              borderColor: "border-blue-200"
            },
            {
              icon: <Truck className="text-green-600" size={32} />,
              title: "Built to Last",
              description: "Constructed with premium-grade materials and rigorous testing to withstand the harshest industrial environments for decades of reliable service.",
              color: "from-green-500/10 to-green-600/10",
              borderColor: "border-green-200"
            },
            {
              icon: <Zap className="text-purple-600" size={32} />,
              title: "Smart Integration",
              description: "Seamless connectivity with your existing systems through advanced IoT capabilities, real-time data streaming, and comprehensive API support.",
              color: "from-purple-500/10 to-purple-600/10",
              borderColor: "border-purple-200"
            }
          ].map((feature, index) => (
            <div
              key={index}
              className={`group bg-gradient-to-br ${feature.color} backdrop-blur-sm p-8 rounded-2xl border ${feature.borderColor} hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2`}
            >
              <div className="bg-white p-4 rounded-2xl w-fit mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">{feature.title}</h3>
              <p className="text-gray-700 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Products Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold mb-4 text-gray-900">Our Professional Scale Collection</h2>
              <p className="text-gray-600 text-lg">Discover precision-engineered solutions for every industrial application</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 mt-6 lg:mt-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                />
              </div>
              
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 rounded-lg transition ${viewMode === 'grid' ? 'bg-white shadow-md text-blue-600' : 'text-gray-500'}`}
                >
                  <Grid size={20} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 rounded-lg transition ${viewMode === 'list' ? 'bg-white shadow-md text-blue-600' : 'text-gray-500'}`}
                >
                  <List size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Enhanced Filter */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => setShowFilter(!showFilter)}
                className="flex items-center text-gray-700 font-semibold hover:text-blue-600 transition"
              >
                <Filter size={20} className="mr-2" />
                Product Categories
                <ChevronDown className={`ml-2 transform transition-transform ${showFilter ? 'rotate-180' : ''}`} size={16} />
              </button>
              <span className="text-sm text-gray-500">{filteredScales.length} products found</span>
            </div>

            {showFilter && (
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 animate-in slide-in-from-top duration-300">
                <div className="flex flex-wrap gap-3">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setActiveTab(category.id)}
                      className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                        activeTab === category.id
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      {category.name}
                      <span className="ml-2 text-xs opacity-75">({category.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className={`grid gap-8 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredScales.map((scale, index) => (
              <div 
                key={scale.id} 
                className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 ${
                  viewMode === 'list' ? 'flex' : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`relative ${viewMode === 'list' ? 'w-80 flex-shrink-0' : ''}`}>
                  <img 
                    src={scale.image} 
                    alt={scale.name} 
                    className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-500" 
                  />
                  
                  {/* Status badges */}
                  <div className="absolute top-4 left-4 flex flex-col gap-2">
                    {scale.featured && (
                      <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        ⭐ Featured
                      </span>
                    )}
                    {/* {scale.originalPrice > scale.price && (
                      <span className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        -{calculateDiscount(scale.originalPrice, scale.price)}% OFF
                      </span>
                    )} */}
                  </div>

                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      scale.inStock 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {scale.inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>

                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition">
                      {scale.name}
                    </h3>
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">{scale.description}</p>
                  
                  <div className="mb-4">
                    {renderStars(scale.rating)}
                  </div>

                  {/* Specifications */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Capacity</p>
                      <p className="font-bold text-gray-900">{scale.capacity}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Accuracy</p>
                      <p className="font-bold text-gray-900">{scale.accuracy}</p>
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="mb-6">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Certifications</p>
                    <div className="flex flex-wrap gap-1">
                      {scale.certifications.map((cert, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Pricing */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      {/* <div className="flex items-center space-x-2">
                        <span className="text-3xl font-bold text-blue-700">{formatPrice(scale.price)}</span>
                        {scale.originalPrice > scale.price && (
                          <span className="text-lg text-gray-500 line-through">{formatPrice(scale.originalPrice)}</span>
                        )}
                      </div> */}
                      <p className="text-sm text-gray-500">Includes installation & training</p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <button className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 flex items-center justify-center shadow-lg">
                      <ShoppingCart size={18} className="mr-2" />
                      View Details
                    </button>
                    <button className="px-4 py-3 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 rounded-xl transition-all duration-200 flex items-center justify-center">
                      <Phone size={18} className="text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enhanced CTA Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready for a Custom 
              <span className="block bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Weighing Solution?
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              Our team of weighing experts will work with you to design and implement 
              the perfect scale solution for your specific industrial requirements, backed by comprehensive support and warranty.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-6 mb-12">
              <button className="group bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center justify-center">
                <Phone className="mr-3 w-5 h-5" />
                Speak with Our Experts
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button className="group border-2 border-white/30 backdrop-blur-sm hover:bg-white/10 px-8 py-4 rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center">
                <Download className="mr-3 w-5 h-5" />
                Download Product Catalog
              </button>
            </div>

            {/* Contact info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="bg-blue-600/20 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-4">
                  <Phone className="w-8 h-8 text-blue-300" />
                </div>
                <h3 className="font-semibold mb-2">Call Us Today</h3>
                <p className="text-blue-300">1-800-SCALES-1</p>
              </div>
              <div className="text-center">
                <div className="bg-green-600/20 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-300" />
                </div>
                <h3 className="font-semibold mb-2">Free Consultation</h3>
                <p className="text-green-300">Expert Guidance Included</p>
              </div>
              <div className="text-center">
                <div className="bg-purple-600/20 backdrop-blur-sm p-4 rounded-2xl w-fit mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-300" />
                </div>
                <h3 className="font-semibold mb-2">Industry Leader</h3>
                <p className="text-purple-300">25+ Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}